import React from "react";

/**
 * TaskTimeClocks — two small analog clocks side-by-side visualising a task's time relative
 * to NOW.
 *
 *   Left clock  : shows the CURRENT time (hour + minute hands)
 *   Right clock : shows the TASK time (hour + minute hands)
 *
 * Behaviour:
 *   · Task in the FUTURE  → right clock's arc from "now" to "task time" tints GREEN. The
 *                            longer the wait, the bigger the green sweep.
 *   · Task is OVERDUE     → left clock's arc from "task time" to "now" tints RED so the
 *                            slippage is visible at a glance.
 *   · No `time` on the task → renders nothing (parent decides layout).
 *
 * Props:
 *   - taskTime  string in "HH:MM" 24h format
 *   - taskDate  optional ISO "YYYY-MM-DD" so we know when the task is (today / another day)
 *
 * The design intent (from the user's request): make TIME UNTIL immediately readable without
 * arithmetic — you can see the sweep grow as the deadline approaches, and it flips red once
 * you're past it.
 */
/** One analog clock face. Hoisted out of `TaskTimeClocks` so React doesn't remount a fresh
 *  component type every render (would tear down the SVG for no reason). */
const Clock = ({ hour, minute, arcFrom, arcTo, arcColor, label, labelColor }) => {
    const size = 68;
    const cx = size / 2, cy = size / 2, r = size / 2 - 3;
    const hourAngle = ((hour % 12) * 30 + minute * 0.5) - 90;
    const minAngle = (minute * 6) - 90;
    const polar = (deg, len) => [cx + Math.cos(deg * Math.PI / 180) * len, cy + Math.sin(deg * Math.PI / 180) * len];
    const [hx, hy] = polar(hourAngle, r * 0.55);
    const [mx, my] = polar(minAngle,  r * 0.80);

    let arcPath = null;
    if (arcColor && arcFrom !== undefined && arcTo !== undefined) {
        let sweep = arcTo - arcFrom;
        if (sweep < 0) sweep += 360;
        if (sweep > 359.5) sweep = 359.5;
        const start = ((arcFrom - 90) * Math.PI) / 180;
        const end   = ((arcFrom + sweep - 90) * Math.PI) / 180;
        const largeArc = sweep > 180 ? 1 : 0;
        const sx = cx + Math.cos(start) * (r - 2), sy = cy + Math.sin(start) * (r - 2);
        const ex = cx + Math.cos(end)   * (r - 2), ey = cy + Math.sin(end)   * (r - 2);
        arcPath = `M ${cx} ${cy} L ${sx} ${sy} A ${r - 2} ${r - 2} 0 ${largeArc} 1 ${ex} ${ey} Z`;
    }

    return (
        <div className="flex flex-col items-center gap-1" data-testid={`task-clock-${label}`}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={cx} cy={cy} r={r} fill="rgba(0,0,0,0.4)" stroke="rgba(212,212,216,0.35)" strokeWidth="1.5" />
                {arcPath && <path d={arcPath} fill={arcColor} opacity="0.35" />}
                {Array.from({ length: 12 }).map((_, i) => {
                    const [x1, y1] = polar(i * 30 - 90, r - 1);
                    const [x2, y2] = polar(i * 30 - 90, r - (i % 3 === 0 ? 5 : 3));
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(212,212,216,0.55)" strokeWidth={i % 3 === 0 ? 1.4 : 0.8} />;
                })}
                <line x1={cx} y1={cy} x2={hx} y2={hy} stroke="#f5f5f4" strokeWidth="2.2" strokeLinecap="round" />
                <line x1={cx} y1={cy} x2={mx} y2={my} stroke="#22d3ee" strokeWidth="1.6" strokeLinecap="round" />
                <circle cx={cx} cy={cy} r={1.6} fill="#f5f5f4" />
            </svg>
            <span className={`text-[10px] font-brand font-bold uppercase tracking-widest ${labelColor}`}>
                {label}
            </span>
        </div>
    );
};

export const TaskTimeClocks = ({ taskTime, taskDate }) => {
    if (!taskTime || !/^\d{2}:\d{2}$/.test(taskTime)) return null;

    const now = new Date();
    const [h24, m] = taskTime.split(":").map(Number);
    // Always compare against TODAY's occurrence of this time — recurring tasks anchor to a
    // past date but their meaningful next beat is TODAY. If we used the raw anchor date the
    // clocks would ALWAYS read "overdue" for any recurring row. Ignore taskDate for the
    // overdue check; it's just kept for label context.
    void taskDate;
    const taskDT = new Date(now);
    taskDT.setHours(h24, m, 0, 0);

    const nowMinutesOfDay  = now.getHours() * 60 + now.getMinutes();
    const taskMinutesOfDay = h24 * 60 + m;
    const isOverdue = taskDT.getTime() < now.getTime();
    const nowAngleMod  = ((nowMinutesOfDay  % 720) / 720) * 360;
    const taskAngleMod = ((taskMinutesOfDay % 720) / 720) * 360;

    const fmt = (dt) => {
        let hh = dt.getHours();
        const mm = String(dt.getMinutes()).padStart(2, "0");
        const ap = hh >= 12 ? "PM" : "AM";
        hh = hh % 12 === 0 ? 12 : hh % 12;
        return `${hh}:${mm} ${ap}`;
    };

    return (
        <div className="flex items-start justify-center mt-1" data-testid="task-time-clocks" data-overdue={isOverdue}>
            {/* Single task-time clock. The pie sweep IS the visual — green from NOW→TASK when
                upcoming (time remaining), red from TASK→NOW when overdue (slippage). One dial
                keeps the focus card lean while still conveying both anchor points via the arc. */}
            <Clock
                hour={h24}
                minute={m}
                arcFrom={isOverdue ? taskAngleMod : nowAngleMod}
                arcTo={isOverdue ? nowAngleMod : taskAngleMod}
                arcColor={isOverdue ? "#ef4444" : "#4ade80"}
                label={`task · ${fmt(taskDT)}`}
                labelColor={isOverdue ? "text-red-400" : "text-emerald-400"}
            />
        </div>
    );
};

export default TaskTimeClocks;
